import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Heart, 
  MessageCircle, 
  Send, 
  Loader2, 
  MoreHorizontal,
  Trash2,
  Image as ImageIcon
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Feed = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [posting, setPosting] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [comments, setComments] = useState({});
  const [newComments, setNewComments] = useState({});
  const [commentLoading, setCommentLoading] = useState({});

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/posts`, { withCredentials: true });
      setPosts(response.data);
    } catch (error) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!newPost.trim()) return;
    
    setPosting(true);
    try {
      const response = await axios.post(`${API_URL}/api/posts`, {
        content: newPost
      }, { withCredentials: true });
      setPosts([response.data, ...posts]);
      setNewPost('');
      toast.success('Post created!');
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setPosting(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      const response = await axios.post(`${API_URL}/api/posts/${postId}/like`, {}, { withCredentials: true });
      setPosts(posts.map(post => 
        post.post_id === postId 
          ? { 
              ...post, 
              likes: response.data.action === 'liked' 
                ? [...post.likes, user.user_id]
                : post.likes.filter(id => id !== user.user_id)
            }
          : post
      ));
    } catch (error) {
      toast.error('Failed to update like');
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await axios.delete(`${API_URL}/api/posts/${postId}`, { withCredentials: true });
      setPosts(posts.filter(p => p.post_id !== postId));
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const toggleComments = async (postId) => {
    const isExpanded = expandedComments[postId];
    setExpandedComments(prev => ({ ...prev, [postId]: !isExpanded }));
    
    if (!isExpanded && !comments[postId]) {
      setCommentLoading(prev => ({ ...prev, [postId]: true }));
      try {
        const response = await axios.get(`${API_URL}/api/posts/${postId}/comments`, { withCredentials: true });
        setComments(prev => ({ ...prev, [postId]: response.data }));
      } catch (error) {
        toast.error('Failed to load comments');
      } finally {
        setCommentLoading(prev => ({ ...prev, [postId]: false }));
      }
    }
  };

  const handleAddComment = async (postId) => {
    const content = newComments[postId];
    if (!content?.trim()) return;
    
    setCommentLoading(prev => ({ ...prev, [postId]: true }));
    try {
      const response = await axios.post(`${API_URL}/api/posts/${postId}/comments`, {
        content
      }, { withCredentials: true });
      setComments(prev => ({ 
        ...prev, 
        [postId]: [...(prev[postId] || []), response.data] 
      }));
      setPosts(posts.map(post => 
        post.post_id === postId 
          ? { ...post, comment_count: post.comment_count + 1 }
          : post
      ));
      setNewComments(prev => ({ ...prev, [postId]: '' }));
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setCommentLoading(prev => ({ ...prev, [postId]: false }));
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Create Post */}
        <Card className="mb-6 border-border" data-testid="create-post-card">
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <Avatar className="h-10 w-10 shrink-0">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <form onSubmit={handleCreatePost} className="flex-1 space-y-3">
                <Textarea
                  placeholder="Share something with your network..."
                  value={newPost}
                  onChange={(e) => setNewPost(e.target.value)}
                  className="min-h-[100px] resize-none border-border focus:border-primary"
                  data-testid="post-content-input"
                />
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={!newPost.trim() || posting}
                    className="rounded-full bg-primary hover:bg-primary/90"
                    data-testid="create-post-btn"
                  >
                    {posting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Post
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </CardContent>
        </Card>

        {/* Posts Feed */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <div className="empty-state">
            <ImageIcon className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No posts yet</h3>
            <p className="text-muted-foreground">Be the first to share something!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {posts.map((post, index) => (
              <Card 
                key={post.post_id} 
                className="post-card animate-fadeIn"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`post-card-${post.post_id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <Link to={`/profile/${post.author_id}`} className="flex items-center gap-3 group">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={post.author_picture} />
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {getInitials(post.author_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-semibold group-hover:text-primary transition-colors">
                          {post.author_name}
                        </p>
                        {post.author_headline && (
                          <p className="text-sm text-muted-foreground">{post.author_headline}</p>
                        )}
                        <p className="text-xs text-muted-foreground">{formatDate(post.created_at)}</p>
                      </div>
                    </Link>
                    {post.author_id === user?.user_id && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem 
                            onClick={() => handleDeletePost(post.post_id)}
                            className="text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete post
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-foreground whitespace-pre-wrap">{post.content}</p>
                  
                  <div className="flex items-center gap-4 pt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleLike(post.post_id)}
                      className={`rounded-full ${post.likes.includes(user?.user_id) ? 'text-red-500' : ''}`}
                      data-testid={`like-btn-${post.post_id}`}
                    >
                      <Heart className={`w-4 h-4 mr-1 ${post.likes.includes(user?.user_id) ? 'fill-current' : ''}`} />
                      {post.likes.length || 0}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleComments(post.post_id)}
                      className="rounded-full"
                      data-testid={`comments-btn-${post.post_id}`}
                    >
                      <MessageCircle className="w-4 h-4 mr-1" />
                      {post.comment_count || 0}
                    </Button>
                  </div>

                  {/* Comments Section */}
                  {expandedComments[post.post_id] && (
                    <div className="pt-4 border-t border-border space-y-4">
                      {commentLoading[post.post_id] && !comments[post.post_id] ? (
                        <div className="flex justify-center py-4">
                          <Loader2 className="w-5 h-5 animate-spin text-primary" />
                        </div>
                      ) : (
                        <>
                          {comments[post.post_id]?.map((comment) => (
                            <div key={comment.comment_id} className="flex gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={comment.author_picture} />
                                <AvatarFallback className="bg-secondary text-secondary-foreground text-xs">
                                  {getInitials(comment.author_name)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 bg-muted rounded-xl px-4 py-2">
                                <p className="text-sm font-medium">{comment.author_name}</p>
                                <p className="text-sm text-foreground">{comment.content}</p>
                              </div>
                            </div>
                          ))}
                          
                          {/* Add Comment */}
                          <div className="flex gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={user?.picture} />
                              <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                                {getInitials(user?.name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 flex gap-2">
                              <input
                                type="text"
                                placeholder="Write a comment..."
                                value={newComments[post.post_id] || ''}
                                onChange={(e) => setNewComments(prev => ({ ...prev, [post.post_id]: e.target.value }))}
                                onKeyPress={(e) => e.key === 'Enter' && handleAddComment(post.post_id)}
                                className="flex-1 bg-muted rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                data-testid={`comment-input-${post.post_id}`}
                              />
                              <Button
                                size="icon"
                                onClick={() => handleAddComment(post.post_id)}
                                disabled={!newComments[post.post_id]?.trim() || commentLoading[post.post_id]}
                                className="rounded-full bg-primary hover:bg-primary/90 h-9 w-9"
                                data-testid={`send-comment-btn-${post.post_id}`}
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Feed;
